/* Hiisi units render — generic .hiisi-measure[data-value][data-unit] swap.
 *
 * Consumer side of the metric/imperial toggle. Foundation (Code B) provides
 * window.HiisiUnits — this file is the renderer that walks the DOM and updates
 * .hiisi-measure elements when the preference changes.
 *
 * If foundation hasn't loaded yet (parallel session), this file installs a
 * minimal mock so consumers (bike-card, my-hiisi summary, etc.) keep working.
 * Foundation's real helper overwrites the mock when it loads. Both paths use
 * the same window.HiisiUnits.format(value, fromUnit) contract.
 *
 * Spec: proposals/spec-metric-imperial-toggle.md
 *
 * Markup contract:
 *   <span class="hiisi-measure" data-value="{grams}" data-unit="g">~16.4 kg</span>
 *   - data-value is ALWAYS the metric storage value (g, cm, kg, mm)
 *   - data-unit is the metric unit it's stored in
 *   - text content is the server-side metric render; JS overwrites on load and
 *     on every hiisi-units-changed event
 *
 * Units convention (per spec, MTB universals NOT toggled):
 *   - g → grams (toggles to lbs in imperial)
 *   - kg → kilograms (toggles to lbs in imperial)
 *   - cm → centimeters (toggles to inches)
 *   - mm → millimeters (NEVER toggles — MTB universal)
 */
(function () {
  'use strict';

  // ── Mock fallback ──────────────────────────────────────────────────────────
  // Only installs if foundation hasn't loaded. The mock matches the contract
  // documented in proposals/prompt-units-toggle-foundation.md §2.
  if (typeof window.HiisiUnits === 'undefined') {
    var mockState = {
      pref: (function () {
        try {
          return (
            window.HIISI_UNITS_PREF ||
            localStorage.getItem('hiisi_units') ||
            'metric'
          );
        } catch (e) {
          return 'metric';
        }
      })(),
    };

    window.HiisiUnits = {
      __mock: true,
      get current() {
        return mockState.pref;
      },
      set current(value) {
        if (value !== 'metric' && value !== 'imperial') return;
        mockState.pref = value;
        try {
          localStorage.setItem('hiisi_units', value);
        } catch (e) {
          /* private mode etc. */
        }
        // Dispatch on document — matches the foundation contract in
        // assets/hiisi-units.js. detail shape uses `value` (not `units`)
        // so listeners are interchangeable between mock and foundation.
        document.dispatchEvent(new CustomEvent('hiisi-units-changed', { detail: { value: value } }));
      },
      cmToIn: function (cm) {
        return cm / 2.54;
      },
      inToCm: function (inches) {
        return inches * 2.54;
      },
      kgToLbs: function (kg) {
        return kg * 2.20462;
      },
      lbsToKg: function (lbs) {
        return lbs / 2.20462;
      },
      // value is ALWAYS in the metric storage unit; we convert on read.
      format: function (value, fromUnit, options) {
        options = options || {};
        var decimals = typeof options.decimals === 'number' ? options.decimals : 1;
        var pref = this.current;
        var num = Number(value);
        if (!isFinite(num)) {
          return { value: value, unit: fromUnit, formatted: '' };
        }

        function fix(n) {
          // Avoid trailing 0 noise on whole numbers ("5.0" → "5") for cm/in
          // when decimals=1 and the value is exact. Weight stays at one decimal
          // intentionally because lbs is rarely a whole number.
          var s = n.toFixed(decimals);
          return s;
        }

        if (pref === 'imperial') {
          switch (fromUnit) {
            case 'g':
              return {
                value: num / 453.592,
                unit: 'lbs',
                formatted: fix(num / 453.592) + ' lbs',
              };
            case 'kg':
              return {
                value: num * 2.20462,
                unit: 'lbs',
                formatted: fix(num * 2.20462) + ' lbs',
              };
            case 'cm':
              return {
                value: num / 2.54,
                unit: 'in',
                formatted: fix(num / 2.54) + ' in',
              };
            // mm intentionally NOT converted — MTB universal.
            case 'mm':
              return { value: num, unit: 'mm', formatted: fix(num) + ' mm' };
            default:
              return { value: num, unit: fromUnit, formatted: fix(num) + ' ' + fromUnit };
          }
        }

        // metric
        switch (fromUnit) {
          case 'g':
            // Display g in kg by default for bike-weight-scale numbers (>= 1000g);
            // keep in g for small deltas. Caller can override via options.preferKg.
            if (options.preferKg || num >= 1000) {
              return {
                value: num / 1000,
                unit: 'kg',
                formatted: fix(num / 1000) + ' kg',
              };
            }
            return { value: num, unit: 'g', formatted: Math.round(num) + ' g' };
          case 'kg':
            return { value: num, unit: 'kg', formatted: fix(num) + ' kg' };
          case 'cm':
            return { value: num, unit: 'cm', formatted: fix(num) + ' cm' };
          case 'mm':
            return { value: num, unit: 'mm', formatted: fix(num) + ' mm' };
          default:
            return { value: num, unit: fromUnit, formatted: fix(num) + ' ' + fromUnit };
        }
      },
    };
  }

  // ── Renderer ───────────────────────────────────────────────────────────────
  // Walks .hiisi-measure[data-value][data-unit] elements and rewrites textContent
  // via window.HiisiUnits.format. Idempotent — safe to re-run on every event.
  function renderAll(root) {
    if (!window.HiisiUnits || typeof window.HiisiUnits.format !== 'function') return;
    var scope = root || document;
    var nodes = scope.querySelectorAll('.hiisi-measure[data-value][data-unit]');
    nodes.forEach(function (el) {
      var rawValue = el.getAttribute('data-value');
      var fromUnit = el.getAttribute('data-unit');
      var decimalsAttr = el.getAttribute('data-decimals');
      var preferKg = el.getAttribute('data-prefer-kg') === 'true';
      var prefix = el.getAttribute('data-prefix') || '';
      var num = parseFloat(rawValue);
      if (!isFinite(num)) return;
      var opts = { preferKg: preferKg };
      if (decimalsAttr !== null) opts.decimals = parseInt(decimalsAttr, 10);
      var out = window.HiisiUnits.format(num, fromUnit, opts);
      el.textContent = prefix + out.formatted;
    });
  }

  // Run once on load. document.readyState may already be 'interactive' or
  // 'complete' when this script executes (loaded with defer or at end of body).
  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  ready(function () {
    renderAll(document);
  });

  // Re-render on every toggle event. Foundation (assets/hiisi-units.js) and
  // the mock above both dispatch on `document` with detail.value === 'metric'|'imperial'.
  document.addEventListener('hiisi-units-changed', function () {
    renderAll(document);
  });

  // Expose for ad-hoc re-render (e.g. after AJAX-injected DOM, or summary swap
  // in consultation-widget after save).
  window.HiisiUnitsRender = { renderAll: renderAll };
})();
